import { TableProps } from '@insight-health-ai/roseate/components';
import { MessageTextSquare01 } from '@insight-health-ai/roseate/icons';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {
  PractitionerName,
  CopyToClipboard,
  getDateRangeFromQuickFilterLabel,
  AppointmentSavedView,
} from 'src/components';
import { UseGetAppointmentListParams } from 'src/hooks/appointment';
import { AppointmentActionsDropdown } from './actions';
import { Appointment } from 'src/models';
import AppointmentStatus from 'src/modules/appointments/components/AppointmentStatus';
import { analytics } from 'src/services';
import { logger } from 'src/services/logger';
import { PromiseOrValue } from '@insight-health-ai/roseate/utils';
import { getPractitionerName } from 'src/utils/practitioner';

export const sortDirectionMap = {
  ascend: 'asc',
  descend: 'desc',
} as const;

/**
 * Filter type (excludes sort and pagination)
 */
export type AppointmentFilters = Omit<
  UseGetAppointmentListParams,
  'sortBy' | 'sortDirection' | 'page' | 'perPage'
>;

/**
 * Sort type
 */
export type AppointmentSort = Pick<
  UseGetAppointmentListParams,
  'sortBy' | 'sortDirection'
>;

/**
 * Default filter values (excludes sort)
 */
export const appointmentDefaultFilters: AppointmentFilters = {};

export const appointmentDefaultView: AppointmentSavedView = {
  id: '__default__',
  title: 'All Today',
  filters: {
    practitionerIds: [],
    inVisitStatus: [],
    chatCompletionStatus: [],
    patientIds: [],
    date: {
      startDate: 'Today',
      endDate: 'Today',
    },
  },
  last_modified_by: null,
  created_by: '',
};

/**
 * Default sort configuration
 */
export const appointmentDefaultSort: AppointmentSort = {
  sortBy: 'start_date',
  sortDirection: 'asc',
};

/** Analytics payload aligned with legacy appointments list (`Appointments.page` / `AppointmentsMenu`). */
export const getAppointmentRowAnalyticsProps = (appointment: Appointment) => {
  const dt = appointment.start_date_local
    ? dayjs(appointment.start_date_local)
    : null;

  return {
    appointment_date: dt
      ? {
          date: dt.format('MMM Do, YYYY hh:mm A'),
          time: dt.format('hh:mm A'),
        }
      : appointment.start_date,
    appointment_id: appointment.appointment_id,
    appointment_status: appointment.chat_completion_status,
    practitioner_name: getPractitionerName({
      first_name: appointment.practitioner_firstName,
      last_name: appointment.practitioner_lastName,
      title: appointment.practitioner_title,
    }),
  };
};

export const trackOpenedCareConnectSummary = (
  appointment: Appointment,
  email: string | undefined,
  source: 'row' | 'menu'
) => {
  const base = {
    ...getAppointmentRowAnalyticsProps(appointment),
    email,
  };
  analytics.track('Opened CareConnect Summary', base);
  if (source === 'menu') {
    analytics.track('Clicked View Summary Action Menu', base);
  }
};

export const getAppointmentListColumns = ({
  sort,
  onEditAppointment,
  userEmail,
}: {
  filters: AppointmentFilters;
  sort: AppointmentSort;
  onEditAppointment: (appointment: Appointment) => PromiseOrValue<void>;
  userEmail?: string;
}) => {
  return [
    {
      title: 'Practitioner',
      dataIndex: 'practitioner_id',
      key: 'practitioner_name',
      sorter: true,
      render: (_: Appointment['practitioner_id'], appointment: Appointment) => {
        return (
          <PractitionerName
            practitioner={{
              first_name: appointment.practitioner_firstName,
              last_name: appointment.practitioner_lastName,
              title: appointment.practitioner_title,
            }}
          />
        );
      },
    },
    {
      title: 'Appointment',
      dataIndex: 'start_date',
      key: 'start_date',
      sorter: true,
      render: (_: Appointment['start_date'], appointment: Appointment) => {
        const isPastDate =
          appointment.start_date_local &&
          dayjs(appointment.start_date_local).isBefore(dayjs());
        return (
          <div
            className={classNames('flex flex-col gap-1', {
              'text-error': isPastDate,
            })}
          >
            <span>
              {dayjs(appointment.start_date_local).format('MMM DD, YYYY')}
            </span>
            <span
              className={classNames('text-xs', {
                'text-gray-500': !isPastDate,
              })}
            >
              {dayjs(appointment.start_date_local).format('hh:mm a')}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Patient',
      dataIndex: 'patient_id',
      key: 'patient_name',
      sorter: true,
      render: (_: Appointment['patient_id'], appointment: Appointment) => {
        const renderChatLink = () => {
          const { llm_chat_url } = appointment;
          let chatUrl = null;

          if (llm_chat_url) {
            try {
              chatUrl = new URL(llm_chat_url);
              chatUrl.searchParams.set('from_care_connect', 'true');
            } catch (error) {
              logger.warn('Invalid chat URL on appointment', {
                llm_chat_url,
                appointmentId: appointment.appointment_id,
              });
            }
          }

          if (
            !chatUrl ||
            (appointment.chat_completion_status as any) === 'Unavailable'
          ) {
            return null;
          }

          return (
            <Tooltip title="View patient chat">
              <a
                href={chatUrl.toString()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  analytics.track('Opened Lumi Chat', {
                    ...getAppointmentRowAnalyticsProps(appointment),
                    email: userEmail,
                    loginType: 'practitioner',
                  });
                }}
              >
                <MessageTextSquare01 className="text-lg text-primary" />
              </a>
            </Tooltip>
          );
        };

        const renderPatientName = () => {
          return (
            <Tooltip
              placement="topLeft"
              title={
                <div className="flex flex-col gap-2">
                  <CopyToClipboard
                    text={String(appointment.patient_id)}
                    copyTooltip={{
                      before: 'Copy IH ID',
                      after: 'IH ID copied!',
                    }}
                  >
                    <span className="font-medium">IH ID:</span>
                    <span className="text-gray-600">
                      {appointment.patient_id}
                    </span>
                  </CopyToClipboard>
                  {appointment.patient_ehr_id && (
                    <CopyToClipboard
                      text={String(appointment.patient_ehr_id)}
                      copyTooltip={{
                        before: 'Copy EHR ID',
                        after: 'EHR ID copied!',
                      }}
                    >
                      <span className="font-medium">EHR ID:</span>
                      <span className="text-gray-600">
                        {appointment.patient_ehr_id}
                      </span>
                    </CopyToClipboard>
                  )}
                </div>
              }
            >
              <span>
                {appointment.patient_firstName} {appointment.patient_lastName}
              </span>
            </Tooltip>
          );
        };

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {renderPatientName()}
              {renderChatLink()}
            </div>
            <span className="text-xs text-gray-500">
              {appointment.birth_date}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Condition',
      dataIndex: 'condition_name',
      key: 'condition_name',
      sorter: true,
      render: (condition_name: Appointment['condition_name']) => {
        return condition_name ? (
          condition_name === 'Unknown' ? (
            'Aura only'
          ) : (
            condition_name
          )
        ) : (
          <span className="text-gray-500 italic">Unknown</span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'chat_completion_status',
      key: 'status',
      render: (_: any, appointment: Appointment) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <AppointmentStatus
              record={{
                ...appointment,
                intakeStatus: appointment.chat_completion_status,
                encounterPreChartStatus: appointment.encounter_pre_chart_status,
                encounterInVisitStatus: appointment.encounter_in_visit_status,
                isProcessed: appointment.is_processed,
              }}
            />
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'appointment_id',
      key: 'actions',
      render: (_: any, appointment: Appointment) => {
        return (
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <AppointmentActionsDropdown
              onEditAppointment={onEditAppointment}
              appointment={appointment}
            />
          </div>
        );
      },
    },
  ].map((column) => {
    const columnObj: NonNullable<TableProps<Appointment>['columns']>[number] = {
      ...column,
      ...(column.key === sort.sortBy && {
        defaultSortOrder: sort.sortDirection === 'desc' ? 'descend' : 'ascend',
      }),
    };
    if (column.sorter) {
      columnObj.sortDirections = ['ascend', 'descend', 'ascend'];
    }

    return columnObj;
  });
};

export const extractFiltersFromRecentSearch = (
  recentSearch: AppointmentSavedView
): { filters: AppointmentFilters } => {
  const {
    practitionerIds,
    inVisitStatus,
    chatCompletionStatus,
    patientIds,
    date,
  } = recentSearch.filters || {};
  return {
    filters: {
      startDate: date?.startDate ?? undefined,
      endDate: date?.endDate ?? undefined,
      practitionerIds:
        practitionerIds && practitionerIds.length > 0
          ? practitionerIds
          : undefined,
      inVisitStatus:
        inVisitStatus && inVisitStatus.length > 0
          ? inVisitStatus[0]
          : undefined,
      chatCompletionStatus:
        chatCompletionStatus && chatCompletionStatus.length > 0
          ? chatCompletionStatus[0]
          : undefined,
      patientId:
        patientIds && patientIds.length > 0 ? patientIds[0] : undefined,
    },
  };
};

export const transformSavedFiltersToApiParams = (
  filters: AppointmentFilters
): UseGetAppointmentListParams => {
  const range = getDateRangeFromQuickFilterLabel(
    filters.startDate || filters.endDate || ''
  );

  const startDate = range?.[0]
    ? dayjs(range[0]).format('YYYY-MM-DD')
    : filters.startDate;
  const endDate = range?.[1]
    ? dayjs(range[1]).format('YYYY-MM-DD')
    : filters.endDate;

  return {
    ...filters,
    startDate,
    endDate,
  };
};

export const transformCurrentFiltersToSavedView = (
  filters: AppointmentFilters
): Pick<AppointmentSavedView, 'filters'> => {
  return {
    filters: {
      practitionerIds: filters.practitionerIds,
      inVisitStatus: filters.inVisitStatus
        ? [filters.inVisitStatus]
        : undefined,
      chatCompletionStatus: filters.chatCompletionStatus
        ? [filters.chatCompletionStatus]
        : undefined,
      patientIds: filters.patientId ? [filters.patientId] : undefined,
      date: {
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    },
  };
};
